/**
 * Masjid Types and Constants
 * Types for Masjid and Floor Plan Template management
 */

/**
 * Floor Codes
 * Standard codes for different floor levels
 */
export const FLOOR_CODES = {
	GROUND: 'G',
	FIRST: 'F1',
	SECOND: 'F2',
	THIRD: 'F3',
	BASEMENT_1: 'B1',
	BASEMENT_2: 'B2',
};

/**
 * Seat Type
 * @typedef {Object} Seat
 * @property {string} _id - Seat ID (assigned by backend)
 * @property {string} number - Seat number (can be numeric or alphanumeric)
 * @property {boolean} isReservedForSpecialGuest - Whether seat is reserved for special guests
 * @property {string} [notes] - Optional notes for the seat
 */

/**
 * Row Type
 * @typedef {Object} Row
 * @property {string} _id - Row ID (assigned by backend)
 * @property {string} rowName - Name of the row
 * @property {Seat[]} seats - Array of seats in this row
 */

/**
 * Floor Type
 * @typedef {Object} Floor
 * @property {string} _id - Floor ID (assigned by backend)
 * @property {string} floorName - Name of the floor
 * @property {string} floorCode - Floor code (G, F1, F2, B1, etc.)
 * @property {number} capacity - Floor capacity
 * @property {Row[]} rows - Array of rows on this floor
 */

/**
 * Floor Plan Template Type
 * @typedef {Object} FloorPlanTemplate
 * @property {Floor[]} floors - Array of floors in the template
 * @property {Date|string} lastUpdated - Last update timestamp
 * @property {number} totalCapacity - Total capacity (auto-calculated by backend)
 */

/**
 * Masjid Type
 * @typedef {Object} Masjid
 * @property {string} _id - Masjid ID
 * @property {string} name - Masjid name
 * @property {string} city - City where masjid is located
 * @property {string} [masjidPicture] - URL to masjid picture
 * @property {FloorPlanTemplate} floorPlanTemplate - Floor plan template
 * @property {Date|string} createdAt - Creation timestamp
 * @property {Date|string} updatedAt - Last update timestamp
 */

/**
 * Create Masjid Request
 * @typedef {Object} CreateMasjidRequest
 * @property {string} name - Masjid name
 * @property {string} city - City name
 * @property {string} [masjidPicture] - URL to masjid picture
 * @property {Object[]} floors - Array of floor definitions
 */

/**
 * Update Masjid Request
 * @typedef {Object} UpdateMasjidRequest
 * @property {string} [name] - Updated masjid name
 * @property {string} [city] - Updated city name
 * @property {string} [masjidPicture] - Updated masjid picture URL
 * @property {FloorPlanTemplate} [floorPlanTemplate] - Updated floor plan template
 */

/**
 * API Response Type
 * @typedef {Object} ApiResponse
 * @property {string} status - Response status ('success' or 'error')
 * @property {number} statusCode - HTTP status code
 * @property {string} message - Response message
 * @property {*} data - Response data
 */

/**
 * Helper function to create a new seat object
 * @param {string} number - Seat number
 * @param {boolean} isReservedForSpecialGuest - Whether reserved for special guest
 * @param {string} notes - Seat notes
 * @returns {Object} Seat object without _id (to be assigned by backend)
 */
export const createSeat = (
	number,
	isReservedForSpecialGuest = false,
	notes = ''
) => ({
	number,
	isReservedForSpecialGuest,
	notes,
});

/**
 * Helper function to create a new row object
 * @param {string} rowName - Row name
 * @param {Array} seats - Array of seats
 * @returns {Object} Row object without _id (to be assigned by backend)
 */
export const createRow = (rowName, seats = []) => ({
	rowName,
	seats,
});

/**
 * Helper function to create a new floor object
 * @param {string} floorName - Floor name
 * @param {string} floorCode - Floor code
 * @param {number} capacity - Floor capacity
 * @param {Array} rows - Array of rows
 * @returns {Object} Floor object without _id (to be assigned by backend)
 */
export const createFloor = (floorName, floorCode, capacity, rows = []) => ({
	floorName,
	floorCode,
	capacity,
	rows,
});

/**
 * Helper function to calculate total capacity from floors
 * @param {Floor[]} floors - Array of floors
 * @returns {number} Total capacity
 */
export const calculateTotalCapacity = (floors) => {
	if (!floors || !Array.isArray(floors)) return 0;

	return floors.reduce((total, floor) => {
		const floorCapacity = floor.rows.reduce((rowTotal, row) => {
			return rowTotal + (row.seats?.length || 0);
		}, 0);
		return total + floorCapacity;
	}, 0);
};

/**
 * Helper function to validate floor plan data
 * @param {Object} floorPlanData - Floor plan data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateFloorPlan = (floorPlanData) => {
	const errors = [];

	if (!floorPlanData) {
		errors.push('Floor plan data is required');
		return { isValid: false, errors };
	}

	if (!floorPlanData.floors || !Array.isArray(floorPlanData.floors)) {
		errors.push('Floors array is required');
		return { isValid: false, errors };
	}

	if (floorPlanData.floors.length === 0) {
		errors.push('At least one floor is required');
		return { isValid: false, errors };
	}

	floorPlanData.floors.forEach((floor, floorIndex) => {
		if (!floor.floorName) {
			errors.push(`Floor ${floorIndex + 1}: Floor name is required`);
		}
		if (!floor.floorCode) {
			errors.push(`Floor ${floorIndex + 1}: Floor code is required`);
		}
		if (!floor.rows || !Array.isArray(floor.rows)) {
			errors.push(`Floor ${floorIndex + 1}: Rows array is required`);
		} else if (floor.rows.length === 0) {
			errors.push(`Floor ${floorIndex + 1}: At least one row is required`);
		} else {
			floor.rows.forEach((row, rowIndex) => {
				if (!row.rowName) {
					errors.push(
						`Floor ${floorIndex + 1}, Row ${rowIndex + 1}: Row name is required`
					);
				}
				if (!row.seats || !Array.isArray(row.seats)) {
					errors.push(
						`Floor ${floorIndex + 1}, Row ${rowIndex + 1}: Seats array is required`
					);
				} else if (row.seats.length === 0) {
					errors.push(
						`Floor ${floorIndex + 1}, Row ${rowIndex + 1}: At least one seat is required`
					);
				} else {
					row.seats.forEach((seat, seatIndex) => {
						if (!seat.number && seat.number !== 0) {
							errors.push(
								`Floor ${floorIndex + 1}, Row ${rowIndex + 1}, Seat ${seatIndex + 1}: Seat number is required`
							);
						}
						if (typeof seat.isReservedForSpecialGuest !== 'boolean') {
							errors.push(
								`Floor ${floorIndex + 1}, Row ${rowIndex + 1}, Seat ${seatIndex + 1}: isReservedForSpecialGuest must be a boolean`
							);
						}
					});
				}
			});
		}
	});

	return {
		isValid: errors.length === 0,
		errors,
	};
};

/**
 * Helper function to validate masjid data
 * @param {Object} masjidData - Masjid data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateMasjidData = (masjidData) => {
	const errors = [];

	if (!masjidData) {
		errors.push('Masjid data is required');
		return { isValid: false, errors };
	}

	if (!masjidData.name || masjidData.name.trim() === '') {
		errors.push('Masjid name is required');
	}

	if (!masjidData.city || masjidData.city.trim() === '') {
		errors.push('City is required');
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
};
